// #region Imports
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import { styled } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useEffect, useMemo } from "react";
import { AvatarCrop } from "marketplace/_views/avatars/avatar-crop";
// #endregion

// #region Styled Components
const StyledAvatar = styled(Avatar)(({ theme }) => ({
  height: theme.spacing(10),
  width: theme.spacing(10),
}));
const LoadingProgress = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.grey[theme.palette.mode === "light" ? 200 : 700],
  height: "75%",
  width: "75%",
}));
const CropPreviewCanvas = styled("canvas")(({ theme }) => ({
  background: theme.palette.action.hover,
  borderRadius: theme.shape.borderRadius,
  display: "block",
  height: theme.spacing(28),
  margin: theme.spacing(0, "auto", 2),
  width: theme.spacing(28),
}));
const CropControls = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(2),
  minWidth: theme.spacing(36),
}));
// #endregion

const CenterCropMark = [{ value: 0.5 }];
const CenterCropValue = 0.5;
const CenterSnapThreshold = 0.03;

// #region Types
export enum AvatarType {
  Individual,
  Company,
  Team,
}
export type AvatarUploadValue = {
  file: File;
  crop?: AvatarCrop;
  previewUrl?: string;
};
// #endregion

// #region Component Props
export interface AvatarUploadProps {
  className?: string;
  accept: string;
  avatar?: File | string | AvatarUploadValue | null;
  onUpload: (updatedAvatar: AvatarUploadValue) => void;
}
// #endregion

// #region Component
export default function AvatarUpload(props: Readonly<AvatarUploadProps>) {
  const { className, accept, avatar, onUpload } = props;

  // #region Refs
  const hiddenAvatarFileInput = React.useRef<HTMLInputElement>(null);
  const cropCanvasRef = React.useRef<HTMLCanvasElement>(null);

  // #region State
  const [imageLoading, setImageLoading] = React.useState<boolean>(false);
  const [cropFile, setCropFile] = React.useState<File | null>(null);
  const [cropImageUrl, setCropImageUrl] = React.useState<string>();
  const [cropImage, setCropImage] = React.useState<HTMLImageElement>();
  const [cropX, setCropX] = React.useState<number>(0.5);
  const [cropY, setCropY] = React.useState<number>(0.5);
  const [zoom, setZoom] = React.useState<number>(1);

  // #region Hooks

  const imageSrc = useMemo(() => {
    if (avatar instanceof File) return URL.createObjectURL(avatar);
    if (avatar && typeof avatar === "object") return avatar.previewUrl ?? URL.createObjectURL(avatar.file);
    return avatar;
  }, [avatar]);

  const crop = useMemo<AvatarCrop | undefined>(() => {
    if (!cropImage) return undefined;
    const cropSize = 1 / zoom;
    return {
      x: cropX,
      y: cropY,
      size: cropSize,
    };
  }, [cropImage, cropX, cropY, zoom]);
  const canMoveCropHorizontally = useMemo(() => {
    if (!cropImage || !crop) return false;
    const sourceSize = Math.min(cropImage.naturalWidth, cropImage.naturalHeight) * crop.size;
    return cropImage.naturalWidth - sourceSize > 1;
  }, [crop, cropImage]);
  const canMoveCropVertically = useMemo(() => {
    if (!cropImage || !crop) return false;
    const sourceSize = Math.min(cropImage.naturalWidth, cropImage.naturalHeight) * crop.size;
    return cropImage.naturalHeight - sourceSize > 1;
  }, [crop, cropImage]);

  useEffect(() => {
    return () => {
      if (imageSrc?.startsWith("blob:")) URL.revokeObjectURL(imageSrc);
    };
  }, [imageSrc]);

  useEffect(() => {
    return () => {
      if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
    };
  }, [cropImageUrl]);

  useEffect(() => {
    const canvas = cropCanvasRef.current;
    if (!canvas || !cropImage || !crop) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    const sourceSize = Math.min(cropImage.naturalWidth, cropImage.naturalHeight) * crop.size;
    const sourceX = crop.x * Math.max(0, cropImage.naturalWidth - sourceSize);
    const sourceY = crop.y * Math.max(0, cropImage.naturalHeight - sourceSize);

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(
      cropImage,
      sourceX,
      sourceY,
      sourceSize,
      sourceSize,
      0,
      0,
      canvas.width,
      canvas.height,
    );
  }, [crop, cropImage]);

  // #region Functions
  function handleAvatarChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;

    if (!files || files.length === 0) return;

    const avatarFile = files[0];

    setCropX(0.5);
    setCropY(0.5);
    setZoom(1);
    setCropFile(avatarFile);
    setCropImage(undefined);
    setCropImageUrl(URL.createObjectURL(avatarFile));
    hiddenAvatarFileInput.current!.value = "";
  }

  function handleCropImageLoaded(event: React.SyntheticEvent<HTMLImageElement>) {
    setCropImage(event.currentTarget);
  }

  function handleCropCancel() {
    setCropFile(null);
    setCropImage(undefined);
    setCropImageUrl(undefined);
  }

  function handleCropConfirm() {
    if (!cropFile) return;

    try {
      onUpload({ file: cropFile, crop, previewUrl: cropCanvasRef.current?.toDataURL("image/webp", 0.82) });
    } catch (error) {
      console.error(error);
    } finally {
      handleCropCancel();
    }
  }

  function handleCenterCropConfirm() {
    if (!cropFile) return;

    try {
      onUpload({ file: cropFile });
    } catch (error) {
      console.error(error);
    } finally {
      handleCropCancel();
    }
  }
  // #endregion

  // #region Render
  return (
    <div className={className}>
      <input
        type="file"
        ref={hiddenAvatarFileInput}
        accept={accept}
        onChange={handleAvatarChange}
        style={{ display: "none" }}
      />
      <Badge
        overlap="rectangular"
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        badgeContent={
          <Tooltip title="Upload avatar">
            <span>
              <IconButton
                color="primary"
                aria-label="upload avatar"
                component="span"
                onClick={() => hiddenAvatarFileInput.current?.click()}
                size="medium"
              >
                {imageLoading ? <CircularProgress size="24px" disableShrink /> : <AddAPhotoIcon />}
              </IconButton>
            </span>
          </Tooltip>
        }
      >
        <StyledAvatar
          src={imageSrc ?? undefined}
          slotProps={{
            img: {
              onLoad: () => setImageLoading(false),
              onError: () => setImageLoading(false),
            },
          }}
        >
          {imageLoading && <LoadingProgress disableShrink />}
        </StyledAvatar>
      </Badge>
      <Dialog open={!!cropFile} onClose={handleCropCancel} maxWidth="xs">
        <DialogTitle>Crop Avatar</DialogTitle>
        <DialogContent>
          {cropImageUrl && (
            <img
              alt=""
              src={cropImageUrl}
              onLoad={handleCropImageLoaded}
              style={{ display: "none" }}
            />
          )}
          <CropPreviewCanvas ref={cropCanvasRef} height={256} width={256} />
          <CropControls>
            <div>
              <Typography variant="caption">Zoom</Typography>
              <Slider aria-label="Zoom" min={1} max={3} step={0.01} value={zoom} onChange={(_, value) => setZoom(value)} />
            </div>
            <div>
              <Typography variant="caption">Horizontal</Typography>
              <Slider
                aria-label="Horizontal crop position"
                marks={CenterCropMark}
                min={0}
                max={1}
                step={0.01}
                value={cropX}
                disabled={!canMoveCropHorizontally}
                onChange={(_, value) => setCropX(snapToCenter(value))}
              />
            </div>
            <div>
              <Typography variant="caption">Vertical</Typography>
              <Slider
                aria-label="Vertical crop position"
                marks={CenterCropMark}
                min={0}
                max={1}
                step={0.01}
                value={cropY}
                disabled={!canMoveCropVertically}
                onChange={(_, value) => setCropY(snapToCenter(value))}
              />
            </div>
          </CropControls>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCropCancel}>Cancel</Button>
          {!cropImage && (
            <Button onClick={handleCenterCropConfirm}>
              Use Center Crop
            </Button>
          )}
          <Button variant="contained" onClick={handleCropConfirm} disabled={!cropImage}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
  // #endregion
}

function snapToCenter(value: number) {
  return Math.abs(value - CenterCropValue) <= CenterSnapThreshold
    ? CenterCropValue
    : value;
}
